/**
 * <StageProgramHeader> — le haut de la page du programme d'un stage acheté (organism).
 *
 * Qui, quand, de quoi le programme est fait (`<ProgramMixBar>`), puis ce que la famille
 * peut faire, selon où elle en est :
 *  - la fenêtre est ouverte → la pastille « Adapter le programme de Léa », et « Jusqu'à
 *    la veille du stage » ; une phrase déjà appliquée → `<AdaptedRequestBanner>` et ses deux suites ;
 *  - avant l'attribution → « Adaptable dès que son masteur est connu » ;
 *  - le stage a commencé → « C'est parti : son masteur l'ajuste en séance », la phrase reste lisible.
 *
 * Peu de mots : la page se lit d'un coup d'œil (maquette validée le 17/10).
 */
import React from "react"

import { Banner } from "../../atoms/Banner"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { AdaptedRequestBanner } from "../../molecules/AdaptedRequestBanner"
import { ProgramMixBar } from "../../molecules/ProgramMixBar"
import { elide } from "../../../ports/primary/ui/formatters/childFormatter"
import { type UIStageProgram } from "../../../ports/primary/ui/types"

export type StageProgramHeaderProps = {
    program: Omit<UIStageProgram, "groups">
    /** Ouvre la feuille « Adapter le programme ». */
    onAdapt: () => void
    /** « Revenir au programme proposé ». */
    onReset: () => void
    /** Une recomposition est en cours : les gestes attendent. */
    busy?: boolean
    testID?: string
}

export function StageProgramHeader(props: StageProgramHeaderProps) {
    const { program, onAdapt, onReset, busy = false, testID } = props
    const { stageTitle, title, childFirstname, dates, state, instructions, mix } = program
    const open = state === "open"

    return (
        <Stack gap="4" testID={testID}>
            <Stack gap="2">
                <Text size="text-xs" weight="bold" color="text-muted" uppercase>
                    {stageTitle}
                </Text>
                <Title size="text-3xl">{title}</Title>
                {dates != null && (
                    <Text size="text-md" color="text-muted">
                        {dates}
                    </Text>
                )}
                {mix != null && <ProgramMixBar mix={mix} />}
            </Stack>

            {instructions != null && (
                <AdaptedRequestBanner
                    instructions={instructions}
                    onEdit={open ? onAdapt : undefined}
                    onReset={open ? onReset : undefined}
                    busy={busy}
                />
            )}

            {open && instructions == null && (
                <Stack gap="2" align="start">
                    <ChoiceChip
                        variant="pill"
                        selected={false}
                        iconName="magic"
                        label={
                            childFirstname
                                ? `Adapter le programme ${elide(childFirstname, "de")}`
                                : "Adapter son programme"
                        }
                        onPress={onAdapt}
                        disabled={busy}
                    />
                    <Row gap="2" align="center">
                        <Icon name="clock" size={14} color="text-muted" />
                        <Text size="text-sm" color="text-muted">
                            Jusqu'à la veille du stage
                        </Text>
                    </Row>
                </Stack>
            )}

            {state === "before_attribution" && (
                <Banner message="Adaptable dès que son masteur est connu" icon="clock" color="primary" tinted />
            )}

            {state === "closed" && (
                <Banner message="C'est parti : son masteur l'ajuste en séance" icon="lock" color="primary" tinted />
            )}
        </Stack>
    )
}
